<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
  @if (receipt) {
    <app-page-detail-hero [auditStamp]="receipt"
      icon="pi pi-file-check"
      eyebrow="Goods Receipt Note"
      [title]="receipt.receiptNumber || 'Stock Receipt'"
      [subtitle]="(receipt.warehouseName || masterName('WAREHOUSE', receipt.warehouseId)) + ' | ' + displayDate(receipt.receiptDate)">
      <div tags>
        <p-tag [value]="receipt.status || 'RECEIVED'" [severity]="statusSeverity(receipt.status || 'RECEIVED')"></p-tag>
        <p-tag [value]="receipt.referenceNumber || 'No external reference'" severity="info"></p-tag>
        <p-tag [value]="lineCount() + ' receipt line' + (lineCount() === 1 ? '' : 's')" severity="secondary"></p-tag>
      </div>
      <div actions>
        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/warehousing/stock-entry"></p-button>
        <p-button label="View Movements" icon="pi pi-history" severity="secondary" [outlined]="true"
          routerLink="/warehousing/stock-ledger"
          [queryParams]="{ referenceNumber: receipt.receiptNumber, movementFrom: receipt.receiptDate, movementTo: receipt.receiptDate }"></p-button>
        <p-button label="Download GRN" icon="pi pi-file-pdf" severity="success" (click)="downloadGrn()"></p-button>
      </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <div class="warehousing-detail-grid">
      <app-page-form-section icon="pi pi-file" title="Receiving Document" subtitle="Receipt reference, status, date, and receiving warehouse.">
        <div class="warehousing-detail-list">
          <div><span>GRN / Receipt #</span><strong>{{ receipt.receiptNumber || '-' }}</strong></div>
          <div><span>External Reference</span><strong>{{ receipt.referenceNumber || '-' }}</strong></div>
          <div><span>Receipt Date</span><strong>{{ displayDate(receipt.receiptDate) }}</strong></div>
          <div><span>Status</span><strong><p-tag [value]="receipt.status || 'RECEIVED'" [severity]="statusSeverity(receipt.status || 'RECEIVED')"></p-tag></strong></div>
          <div><span>Receiving Warehouse</span><strong>{{ receipt.warehouseName || masterName('WAREHOUSE', receipt.warehouseId) }}</strong></div>
          <div><span>Total Base Quantity</span><strong>{{ receipt.totalQuantity || 0 | number:'1.0-4' }}</strong></div>
        </div>
      </app-page-form-section>

      <app-page-form-section icon="pi pi-truck" title="Supplier and Delivery" subtitle="Supplier, carrier, driver, and vehicle data entered while receiving.">
        <div class="warehousing-detail-list">
          <div><span>Supplier</span><strong>{{ receipt.supplierName || '-' }}</strong></div>
          <div><span>Driver</span><strong>{{ receipt.driverName || '-' }}</strong></div>
          <div><span>Vehicle Plate</span><strong>{{ receipt.vehiclePlateNumber || '-' }}</strong></div>
          <div><span>Vehicle ID</span><strong>{{ receipt.vehicleId || '-' }}</strong></div>
        </div>
      </app-page-form-section>
    </div>

    <app-page-form-section icon="pi pi-align-left" title="Receipt Notes" subtitle="Notes captured for the complete receipt.">
      <p class="warehousing-detail-note">{{ receipt.notes || 'No receipt notes provided.' }}</p>
    </app-page-form-section>

    <app-page-form-section *ngIf="receipt.attachments?.length" icon="pi pi-paperclip" title="Delivery Note Attachments" subtitle="Documents attached to this receipt.">
      <div class="flex flex-column gap-2">
        <a *ngFor="let attachment of receipt.attachments" [href]="attachment.fileUrl" target="_blank" rel="noopener">
          <i class="pi pi-paperclip"></i> {{ attachment.fileName }}
        </a>
      </div>
    </app-page-form-section>

    <app-page-data-panel title="Received Items" subtitle="Every item, its placement and quantity. Use Correct Quantity while its received stock is untouched." [resultText]="lineCount() + ' records'">
      <p-table [value]="receipt.lines || []" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [tableStyle]="{ 'min-width': '112rem' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Item and Identifiers</th>
            <th>Description and Size</th>
            <th>Owner and Placement</th>
            <th>Tracking and Unit SKUs</th>
            <th class="text-right">Quantity</th>
            <th>Line Notes</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-line>
          <tr>
            <td>
              <div class="stack-cell">
                <span class="cell-title">{{ itemLabel(line) }}</span>
                <div class="cell-meta vertical">
                  <span><i class="pi pi-barcode"></i> Receipt SKU: {{ line.skuNumber || '-' }}</span>
                  <span><i class="pi pi-tag"></i> Customer SKU: {{ line.customerSkuNumber || '-' }}</span>
                  <span><i class="pi pi-box"></i> Master SKU: {{ itemFor(line)?.skuNumber || '-' }}</span>
                  <span><i class="pi pi-file"></i> Entry: {{ line.entryNumber || '-' }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="stack-cell">
                <span>{{ itemDescription(line) }}</span>
                <div class="cell-meta vertical">
                  <span><i class="pi pi-expand"></i> {{ itemSize(line) }}</span>
                  <span><i class="pi pi-calculator"></i> {{ itemDimensions(line) }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="stack-cell">
                <span class="cell-title">{{ masterName('WAREHOUSE', line.warehouseId) }}</span>
                <div class="cell-meta vertical">
                  <span><i class="pi pi-sitemap"></i> {{ masterName('REGION', line.regionId) }} / {{ masterName('ZONE', line.zoneId) }}</span>
                  <span><i class="pi pi-check-circle"></i> {{ masterName('ITEM_CONDITION', line.conditionId) }}</span>
                  <span><i class="pi pi-building"></i> {{ lineClientName(line) }} / {{ lineAccountName(line) }}</span>
                  <span><i class="pi pi-user"></i> {{ line.stockOwnerType || 'CRM_CLIENT_OWNED' }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="stack-cell">
                <p-tag [value]="trackingMode(line)" [severity]="trackingMode(line) === 'UNIT' ? 'warn' : 'secondary'"></p-tag>
                <div class="cell-meta vertical mt-2">
                  <span><i class="pi pi-qrcode"></i> {{ unitSkuLabel(line) }}</span>
                  <span><i class="pi pi-circle"></i> Initial status: {{ line.initialStatus || 'AVAILABLE' }}</span>
                </div>
              </div>
            </td>
            <td class="text-right">
              <div class="stack-cell number-stack">
                <strong>{{ enteredQuantity(line) | number:'1.0-4' }} {{ transactionUomName(line) }}</strong>
                <span class="text-500">Base: {{ line.quantity | number:'1.0-4' }}</span>
                <span class="text-500" *ngIf="line.packageQuantity != null">Packages: {{ line.packageQuantity | number:'1.0-4' }}</span>
                <span class="text-500">Received: {{ displayDate(line.entryDate) }}</span>
                <p-button *ngIf="canEditStockEntries && line.id" label="Correct Quantity" icon="pi pi-pencil" size="small" severity="secondary" [outlined]="true"
                  [routerLink]="['/warehousing/stock-entry/edit', line.id]"></p-button>
              </div>
            </td>
            <td>{{ line.notes || 'No line notes provided.' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="6"><div class="warehousing-empty-state"><i class="pi pi-inbox"></i><strong>No receipt lines</strong><span>This receipt does not contain any item lines.</span></div></td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  } @else if (!isLoading) {
    <div class="warehousing-empty-detail">
      <i class="pi pi-search"></i>
      <strong>Receipt not found</strong>
      <span>The selected goods receipt could not be loaded.</span>
    </div>
  }
</app-page-shell>
